import _ from 'lodash';
import Category from 'models/Category';

/**
 * The left-hand block of the ledger: the spreadsheet's A3:D34.
 *
 * Her sheet lists every category she budgets against, under a bold group heading, with
 * Budgetted / Spent / Remain beside it and a grand total at the bottom. There are no per-group
 * subtotals in the original -- the headings are bare -- so there are none here either.
 *
 * The three types below are the ones whose budgets are money leaving: her block ran loan payments
 * ("Student Loan Payments", "Car Loan") and savings straight through it alongside rent and
 * groceries, and only income was kept out, in a region of its own further down the sheet. Income
 * has no meaningful "Remain" against a budget, which is the same reason it does not belong here.
 */
export const BUDGET_BLOCK_TYPES = [
	Category.types.EXPENSE,
	Category.types.SAVINGS,
	Category.types.LOANS,
];

/**
 * Rows for the budget block, already grouped and totalled.
 *
 * Every amount comes from `getMultiBudget(selectedDates)`, which sums across whatever months are
 * selected. That is what makes a multi-month selection additive for free -- one month or six, the
 * block is built exactly the same way.
 *
 * @param categoryGroupsByType {Object} from `getAllCategoryGroupsHashedByType()`
 * @param selectedDates {Object} the dashboard's selected months, keyed by year-month
 * @returns {{groups: Array, totals: {budgeted: number, spent: number, remaining: number}}}
 */
export default function budgetBlockRows(categoryGroupsByType, selectedDates, types = BUDGET_BLOCK_TYPES) {
	const groups = [];
	const totals = { budgeted: 0, spent: 0, remaining: 0 };

	types.forEach((type) => {
		_.each(categoryGroupsByType[type] || [], (categoryGroup) => {
			const rows = [];
			// `true` here reads backwards but is right: it drops the categories the user has
			// already asked not to see when they are unused. See CategoryGroup.
			categoryGroup.getVisibleCategoryForMonths(selectedDates, true).forEach((category) => {
				const budget = category.getMultiBudget(selectedDates);
				const row = {
					category,
					// One level is all `CategoryGroup.sortCategories()` builds, and all the block
					// draws: a child sits directly under its parent, indented once.
					depth: category.parent_category_id ? 1 : 0,
					hasChildren: false,
					sharesBudgetWithChildren: false,
					usesParentBudget: false,
					budgeted: budget.getBudgeted(),
					spent: budget.getSpent(),
					remaining: budget.getRemaining(),
				};
				// The totals are of what each category holds on its own, taken before the roll-up
				// below moves any of it upward -- adding a master parent's rolled-up numbers to
				// its children's would count the same money twice.
				totals.budgeted += row.budgeted;
				totals.spent += row.spent;
				totals.remaining += row.remaining;
				rows.push(row);
			});
			// A heading with nothing under it is noise, not structure.
			if(rows.length) {
				groups.push({ categoryGroup, rows: rollUpMasterBudgets(rows) });
			}
		});
	});

	return { groups, totals };
}

/**
 * What a master budget's row says once its children are drawn under it.
 *
 * `is_master_budget_for_children` means the parent holds the money and the children spend it:
 * Antoine Extra is budgeted $120 and Food, Beauty and Computer under it are budgeted nothing at
 * all. Row by row that reads as a lie in both directions -- the parent looks like it has spent
 * nothing while $110 has gone out through its children, and each child shows a Remain of minus
 * whatever it spent, against a budget it was never meant to have. So the parent's three numbers
 * are the family's, which is what the dashboard's own arrows have always said
 * (`categorySummaryCalculations.getMasterBudgetAmounts()`).
 *
 * Only the children on screen are folded in. One that is filtered out has no budget and no
 * activity in these months -- that is what filtered it out -- so it has nothing to contribute.
 *
 * `usesParentBudget` is what the block draws an arrow for instead of an amount, and it is off for
 * a child carrying an amount of its own: an arrow over a real $40 would hide money. The roll-up
 * still takes that $40 upward, so the parent's row stays the sum of the family either way.
 */
function rollUpMasterBudgets(rows) {
	const childRows = new Map();
	rows.forEach((row) => {
		const parentId = row.category.parent_category_id;
		if(!parentId) {
			return;
		}
		if(!childRows.has(parentId)) {
			childRows.set(parentId, []);
		}
		childRows.get(parentId).push(row);
	});

	rows.forEach((row) => {
		const children = childRows.get(row.category.id) || [];
		row.hasChildren = !!children.length;
		row.sharesBudgetWithChildren = row.hasChildren && !!row.category.is_master_budget_for_children;
		if(!row.sharesBudgetWithChildren) {
			return;
		}
		children.forEach((child) => {
			child.usesParentBudget = child.budgeted === 0;
			row.budgeted += child.budgeted;
			row.spent += child.spent;
			row.remaining += child.remaining;
		});
	});

	return rows;
}
